import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Database, FileText, Layers, CheckCircle2 } from 'lucide-react';

const FinancialDataIntelligence = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#060D1B] via-[#0A1628] to-[#0d1f3c]">
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="text-center">
            <div className="inline-block px-3 py-1 bg-[#0E7490]/20 text-[#0E7490] text-xs font-semibold uppercase tracking-wider rounded-full mb-4">
              Intelligence Foundation
            </div>
            <h1 className="text-h1 text-white mb-6">Financial Data Intelligence</h1>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto mb-8">
              Transform raw financial data into decision-ready intelligence with automated extraction, validation, and enrichment pipelines.
            </p>
            <Button 
              asChild 
              size="lg" 
              className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold text-lg px-8 h-14 btn-hover-lift shadow-lg shadow-[#1A56DB]/30"
            >
              <Link to="/contact">Request Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="section-padding px-6 bg-[#EFF6FF]">
        <div className="max-w-[1200px] mx-auto">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <h2 className="text-h3 text-[#0A1628] mb-4">Core Capabilities</h2>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              Comprehensive data ingestion, transformation, and feature engineering for BFSI
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#0E7490]/10 rounded-lg w-fit mb-3">
                  <FileText className="text-[#0E7490]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">Document Intelligence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B7280] text-sm mb-4">
                  Extract, parse, classify and categorize financial documents with advanced OCR and NLP.
                </p>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {['OCR & Document Parsing', 'Entity Extraction', 'Document Classification', 'Information Extraction'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#0E7490]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#0E7490]/10 rounded-lg w-fit mb-3">
                  <Database className="text-[#0E7490]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">AA Data Intelligence</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B7280] text-sm mb-4">
                  Ingest data from Account Aggregators, bank statements, GST, ITR and transform into intelligence.
                </p>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {['Transaction Enrichment', 'Cashflow Analysis', 'Income/Expense Patterns', 'Risk Signal Detection'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#0E7490]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border-[#D1D5DB] rounded-xl shadow-sm hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <div className="p-3 bg-[#0E7490]/10 rounded-lg w-fit mb-3">
                  <Layers className="text-[#0E7490]" size={24} />
                </div>
                <CardTitle className="text-[#0A1628] text-xl">100+ Credit-Ready Features</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#6B7280] text-sm mb-4">
                  Pre-built features covering income, affordability, spend behavior, and risk signals.
                </p>
                <ul className="space-y-2 text-sm text-[#374151]">
                  {['Credit Scoring Enhancement', 'Behavioral Analysis', 'Ability-to-Pay Assessment', 'Red Flag Detection'].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <CheckCircle2 size={14} className="text-[#0E7490]" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Build Your Intelligence Foundation
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            See how Financial Data Intelligence can transform your credit operations
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
          >
            <Link to="/contact">Request a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FinancialDataIntelligence;
